import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { readAuthConfig } from '../auth/authConfig'
import { probeEmailAuthoritative } from '../auth/emailProbe'
import {
  completeNewPassword,
  confirmForgotPassword,
  confirmSignUp,
  forgotPassword,
  resendCode,
  signIn,
  signUp,
  AuthFlowError,
  type NewPasswordPending,
} from '../auth/cognitoAuth'
import { normalizeEmail } from '../auth/normalizeEmail'
import { RETURN_TO_PARAM, currentReturnPath, sanitizeReturnTo } from '../auth/sessionExpiry'
import { storeSession } from '../auth/session'
import type { TokenSet } from '../auth/oidc'
import { markLegacyPrefillPending } from '../data/myApi'
import { loadLegacyDraft } from './wizard/profileStore'
import { Brand, Button } from '../components/ui'

/*
 * EMAIL-FIRST, fully in-app login (no hosted UI — the browser NEVER navigates
 * to *.amazoncognito.com). One component, one state machine, no new routes:
 *
 *   EMAIL → CHECKING → (no-account/exists/indet) SET_PASSWORD →
 *                          signUp ok        → CONFIRM_CODE → AUTHING → DONE
 *                          UsernameExists   → signIn(entered pw):
 *                                               ok    → DONE
 *                                               wrong → PASSWORD → DONE
 *                     (unconfirmed)  CONFIRM_CODE(resume) → PASSWORD
 *                     (reset-req.)   FORGOT_SEND → FORGOT_CODE → PASSWORD
 *   PASSWORD / SET_PASSWORD  "Forgot?" → FORGOT_SEND → FORGOT_CODE → PASSWORD
 *     (BOARDROOM-2026-09-01-180926: forgot is on BOTH password steps; a hard
 *      failure returns to the step it came from; EMAIL carries an explicit
 *      "New to Trumin? Create an account" → SET_PASSWORD, no probe)
 *   PASSWORD  newPasswordRequired → NEW_PASSWORD → DONE
 *   DONE = storeSession(tokens) then window.location.replace('/').
 *
 * IDENTIFIER-FIRST, dead-end fix (BOARDROOM-2026-08-14-095149): a NEW buyer
 * arriving from the order-confirmation "Go to My Trumin" link must land on
 * account CREATION, never on a sign-in "Incorrect email or password" dead-end.
 * The consumer pool has PreventUserExistenceErrors ENABLED, so the passwordless
 * SRP probe (emailProbe.ts) returns PASSWORD_VERIFIER ('exists') even for
 * brand-NEW emails — it CANNOT tell a new buyer from a returning one. So we no
 * longer let the probe gate a new email into sign-in: every ambiguous result
 * routes to the NEUTRAL SET_PASSWORD step, which CREATES the account for a new
 * email (self-signUp) and transparently signs in an existing one (the proven
 * ClaimLanding smart-path). A new email therefore never reaches a sign-in error.
 * The email can be prefilled via the deep-link (?email=…) and auto-checked.
 *
 * Auth calls go through src/auth/cognitoAuth.ts (USER_SRP_AUTH over
 * amazon-cognito-identity-js); session.ts stays the single session source and
 * the app's auth gate remains loadSession() !== null.
 *
 * AUTHORITATIVE DISCRIMINATOR (CMT-2026-08-28-006, BOARDROOM-2026-08-27-214424):
 * the my-api existence endpoint recommended above is now BUILT — POST
 * /account/probe {email} → {status} via AdminGetUser (grant already held).
 * probeEmailAuthoritative consults it first: 'confirmed' routes a returning
 * user straight to the PASSWORD ("Enter password") step; 'not-found' to
 * CREATE. When my-api is unreachable the SRP probe below remains the floor
 * (every ambiguous result still lands on the neutral SET_PASSWORD step), so
 * an api outage can never make login worse than the pre-fix behaviour.
 *
 * Legacy account marry-up (CMT-035, Approach B = JIT link-on-first-login): the
 * NEW-ACCOUNT branch (CREATE_PASSWORD → …→ auto sign-in) checks, once the
 * session exists, whether this verified email matches a legacy account. On a
 * match the profile wizard OPENS PRE-FILLED for review (finishNewAccount below);
 * no match ⇒ the normal home redirect, unchanged. The datalake LOOKUP is server-
 * side + currently pending, so today this always resolves "no match" — the
 * wiring is inert-but-ready. PII never enters a URL/query or storage (only a
 * boolean marker; the wizard re-fetches the profile via the authed endpoint).
 */

type Step =
  | 'EMAIL'
  | 'CHECKING'
  | 'PASSWORD'
  | 'CREATE_PASSWORD'
  | 'CONFIRM_CODE'
  | 'AUTHING'
  | 'NEW_PASSWORD'
  | 'FORGOT_SEND'
  | 'FORGOT_CODE'

// Styles — inputs per ProfileWizard inputStyle (canon §5); frame per the
// existing Login (360 column, Brand 34, "My Trumin").
const inputStyle: CSSProperties = {
  width: '100%', minHeight: 46, border: '1px solid var(--hairline-14)',
  borderRadius: 11, padding: '0 13px', fontSize: 15, background: 'var(--bg-frame)',
  color: 'var(--text)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
}
const labelStyle: CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6,
}
const linkBtnStyle: CSSProperties = {
  border: 0, background: 'transparent', color: 'var(--accent-bright)', fontFamily: 'inherit',
  fontSize: 12.5, fontWeight: 700, cursor: 'pointer', padding: 0,
}

/** Mask an email for the confirm-code heading — first char + domain kept. */
function maskEmail(email: string): string {
  const at = email.indexOf('@')
  if (at <= 0) return email
  const local = email.slice(0, at)
  const domain = email.slice(at)
  const head = local[0]
  return `${head}${'•'.repeat(Math.max(1, local.length - 1))}${domain}`
}

const RESEND_COOLDOWN_S = 30

/** Forgot-password notice — HONEST under anti-enumeration (BOARDROOM-2026-09-
 *  11-115601, the dave@cab-sports.com silence): Cognito's
 *  PreventUserExistenceErrors makes a reset request on a NON-existent address
 *  "succeed" with no email ever arriving, so an unconditional "We emailed you
 *  a reset code" strands that user in silence. The copy states the truth the
 *  flow can actually promise — and names the no-code exit (the address has no
 *  account → create one) without confirming existence either way. */
const FORGOT_SENT_NOTICE =
  'If an account exists for this address, a reset code is on its way — check spam too. '
  + 'No code after a few minutes usually means no account uses this exact address: go back and create an account instead.'

export function Login() {
  // Email-first only needs pool/client/region — NOT a hosted-UI domain.
  const configured = useMemo(() => readAuthConfig() !== null, [])

  // The order-confirmation deep-link carries the buyer's known email
  // (?email=…). We prefill it and auto-run the existence check so a new buyer
  // is taken straight into account creation — "we know the email" (Duncan's
  // ruling, BOARDROOM-2026-08-14-095149).
  const prefillEmail = useMemo(() => {
    try {
      return (new URLSearchParams(window.location.search).get('email') ?? '').trim()
    } catch {
      return ''
    }
  }, [])

  // RETURN PATH (CMT-2026-09-03-026): where sign-in lands afterwards.
  // Priority: an explicit ?returnTo=… (the session-expiry redirect carries the
  // path the user was on) → else the deep path Login itself rendered at (the
  // unauthed gate shows Login at ANY route, so a cold entry on /orders/x must
  // return to /orders/x, not '/'). Both validated same-origin; '/' ⇒ null.
  const returnTo = useMemo(() => {
    try {
      const explicit = sanitizeReturnTo(new URLSearchParams(window.location.search).get(RETURN_TO_PARAM))
      if (explicit) return explicit
      return window.location.pathname !== '/' ? sanitizeReturnTo(currentReturnPath()) : null
    } catch {
      return null
    }
  }, [])

  const [step, setStep] = useState<Step>('EMAIL')
  const [email, setEmail] = useState(prefillEmail)
  const [password, setPassword] = useState('') // the field for the current step
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [resend, setResend] = useState(0) // cooldown seconds remaining

  // A password captured at CREATE_PASSWORD is held IN MEMORY so a successful
  // CONFIRM_CODE can auto-sign-in (never persisted, never logged).
  const createdPassword = useRef<string | null>(null)
  // Live CognitoUser challenge for the NEW_PASSWORD state.
  const pendingNewPw = useRef<NewPasswordPending | null>(null)
  // One-shot guard so a prefilled deep-link email auto-advances only once.
  const autoAdvanced = useRef(false)
  // The case-CORRECT Cognito username the probe resolved this email to
  // (Duncan checklist #3, BOARDROOM-2026-09-02-191616). The pool is case-
  // sensitive and legacy usernames were minted as-typed, so the FORGOT flow
  // must target this value — a lowercased mixed-case username makes
  // ForgotPassword a silent no-op (PreventUserExistenceErrors simulates
  // success). null (probe skipped/failed) falls back to the canonical
  // lowercase form. Display keeps whatever the user typed; only the wire
  // identity lives here.
  const resolvedUsername = useRef<string | null>(null)
  const authUsername = () => resolvedUsername.current ?? normalizeEmail(email)

  // Resend cooldown ticker.
  useEffect(() => {
    if (resend <= 0) return
    const t = setInterval(() => setResend((s) => (s <= 1 ? 0 : s - 1)), 1000)
    return () => clearInterval(t)
  }, [resend])

  const emailValid = /.+@.+\..+/.test(email.trim())

  // Deep-link auto-advance: a valid prefilled email runs the existence check
  // immediately, so the buyer never re-types the address they just bought with.
  useEffect(() => {
    if (autoAdvanced.current) return
    if (!configured || !prefillEmail || !emailValid) return
    autoAdvanced.current = true
    void submitEmail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function resetTransientAt(next: Step) {
    setError(null)
    setNotice(null)
    setPassword('')
    setCode('')
    setStep(next)
  }

  function fail(e: unknown): AuthFlowError {
    const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', e instanceof Error ? e.message : 'Something went wrong.')
    setError(mapErrorCopy(err))
    return err
  }

  function finish(tokens: TokenSet) {
    createdPassword.current = null
    storeSession(tokens)
    // Land back where the session died / where the deep link pointed
    // (CMT-2026-09-03-026) — home stays the default.
    window.location.replace(returnTo ?? '/')
  }

  /**
   * Finish for the NEW-ACCOUNT branch (CMT-035 marry-up). Stores the session,
   * then checks for a legacy match: on a hit, mark the one-shot pre-fill marker
   * and open the profile wizard (pre-filled for review); otherwise the normal
   * home redirect. The legacy check is best-effort — any failure (or no api
   * base / pending datalake lookup) degrades silently to the home redirect and
   * NEVER blocks account creation.
   */
  async function finishNewAccount(tokens: TokenSet) {
    createdPassword.current = null
    storeSession(tokens)
    try {
      const legacy = await loadLegacyDraft()
      if (legacy) {
        markLegacyPrefillPending()
        window.location.replace('/get-ready/profile/step-1')
        return
      }
    } catch {
      // never block a brand-new sign-in on a legacy-lookup hiccup
    }
    // No legacy review ⇒ same return-path landing as finish() (the legacy
    // wizard review above deliberately outranks returnTo — it is the one-shot
    // account-marry-up moment and must not be skipped).
    window.location.replace(returnTo ?? '/')
  }

  // --- transitions ---------------------------------------------------------

  async function submitEmail() {
    if (!emailValid || busy) return
    setError(null)
    setNotice(null)
    setBusy(true)
    setStep('CHECKING')
    resolvedUsername.current = null
    try {
      const probe = await probeEmailAuthoritative(email.trim())
      resolvedUsername.current = probe.username
      setBusy(false)
      switch (probe.result) {
        case 'exists-confirmed':
          // AUTHORITATIVE existing-account signal (my-api /account/probe via
          // AdminGetUser — CMT-2026-08-28-006): a returning user goes straight
          // to "Enter password", never the create branch.
          resetTransientAt('PASSWORD')
          break
        case 'unconfirmed':
          createdPassword.current = null // resume path — no held password
          resetTransientAt('CONFIRM_CODE')
          setNotice('We emailed you a code to confirm this address.')
          break
        case 'reset-required':
          await beginForgot()
          break
        case 'no-account':
        case 'exists':
        case 'indeterminate':
        default:
          // 'exists' here is the AMBIGUOUS SRP-probe fallback (my-api probe
          // unreachable): PreventUserExistenceErrors makes it fire even for
          // brand-NEW emails, so we CANNOT trust it to send a new buyer into
          // sign-in (the launch dead-end). Route every ambiguous result to the
          // neutral SET_PASSWORD step, which creates the account for a new
          // email and signs an existing one straight in — a new email never
          // reaches an "Incorrect email or password" error.
          resetTransientAt('CREATE_PASSWORD')
          break
      }
    } catch {
      // Probe surfaced an error (e.g. SRP flow off) — go to SET_PASSWORD (which
      // handles both new and existing) rather than blocking or dead-ending.
      setBusy(false)
      resetTransientAt('CREATE_PASSWORD')
    }
  }

  async function submitPassword() {
    if (!password || busy) return
    setError(null)
    setBusy(true)
    try {
      const result = await signIn(email.trim(), password)
      if (result.status === 'newPasswordRequired') {
        pendingNewPw.current = result.pending
        setBusy(false)
        resetTransientAt('NEW_PASSWORD')
        return
      }
      finish(result.tokens)
    } catch (e) {
      setBusy(false)
      const err = fail(e)
      // We only reach PASSWORD for an account we already KNOW exists (the
      // SET_PASSWORD crossover, or the unconfirmed/reset resume) — so a failure
      // here is a wrong password, never a missing account. Never imply the
      // email is wrong; point the user at the reset option below.
      if (err.code === 'NotAuthorized' || err.code === 'UserNotFound') {
        setError('That password isn’t right — try again, or reset it below.')
      }
    }
  }

  async function submitCreatePassword() {
    if (!password || busy) return
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      const res = await signUp(email.trim(), password)
      createdPassword.current = password // held for auto sign-in after confirm
      setBusy(false)
      if (res.userConfirmed) {
        // Already confirmed (rare) — sign straight in.
        await autoSignIn()
      } else {
        resetTransientAt('CONFIRM_CODE')
      }
    } catch (e) {
      const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', 'Something went wrong.')
      if (err.code === 'UsernameExists') {
        // The account already EXISTS (signUp is our authoritative existence
        // signal — the SRP probe can't tell, PreventUserExistenceErrors). Try
        // the entered password as the EXISTING one (ClaimLanding smart-path):
        // correct → straight in; wrong → the pure sign-in step with a reset
        // option. We never accuse a NEW email of a bad password.
        try {
          const result = await signIn(email.trim(), password)
          if (result.status === 'newPasswordRequired') {
            pendingNewPw.current = result.pending
            setBusy(false)
            resetTransientAt('NEW_PASSWORD')
            return
          }
          finish(result.tokens)
        } catch {
          createdPassword.current = null
          setBusy(false)
          resetTransientAt('PASSWORD')
          setNotice('You already have an account — enter your password.')
        }
        return
      }
      setBusy(false)
      if (err.code === 'InvalidPassword' || err.code === 'InvalidParameter') {
        // Render the SERVER's password-policy message verbatim (spec).
        setError(err.message)
      } else {
        setError(mapErrorCopy(err))
      }
    }
  }

  async function autoSignIn() {
    const pw = createdPassword.current
    if (!pw) {
      // No held password (resume path) — go sign in manually.
      resetTransientAt('PASSWORD')
      setNotice('Email confirmed — sign in.')
      return
    }
    setBusy(true)
    setStep('AUTHING')
    try {
      const result = await signIn(email.trim(), pw)
      if (result.status === 'newPasswordRequired') {
        pendingNewPw.current = result.pending
        setBusy(false)
        resetTransientAt('NEW_PASSWORD')
        return
      }
      // NEW-ACCOUNT branch: check for a legacy match → pre-filled wizard review
      // (CMT-035). Falls back to the home redirect on no match / failure.
      await finishNewAccount(result.tokens)
    } catch (e) {
      setBusy(false)
      resetTransientAt('PASSWORD')
      fail(e)
    }
  }

  async function submitConfirmCode() {
    if (code.trim().length < 6 || busy) return
    setError(null)
    setBusy(true)
    try {
      await confirmSignUp(email.trim(), code.trim())
      setBusy(false)
      if (createdPassword.current) {
        await autoSignIn()
      } else {
        resetTransientAt('PASSWORD')
        setNotice('Email confirmed — sign in.')
      }
    } catch (e) {
      setBusy(false)
      const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', 'Something went wrong.')
      if (err.code === 'CodeMismatch') {
        setError("That code isn't right — check the newest email.")
      } else if (err.code === 'ExpiredCode') {
        setError('That code has expired — tap Resend code for a fresh one.')
      } else {
        setError(mapErrorCopy(err))
      }
    }
  }

  async function doResend() {
    if (resend > 0 || busy) return
    setError(null)
    try {
      // FORGOT_CODE's code comes from ForgotPassword, not the signup lane —
      // resendConfirmationCode errors for a CONFIRMED user (the reset case).
      // Re-invoke the matching sender per step; UserNotFound stays neutral
      // (same non-enumeration rule as beginForgot).
      if (step === 'FORGOT_CODE') {
        try {
          await forgotPassword(authUsername(), { exactUsername: true })
        } catch (e) {
          const err = e instanceof AuthFlowError ? e : null
          if (!err || err.code !== 'UserNotFound') throw e
        }
      } else {
        await resendCode(email.trim())
      }
      setNotice('We sent a fresh code.')
      setResend(RESEND_COOLDOWN_S)
    } catch (e) {
      fail(e)
    }
  }

  async function submitNewPassword() {
    if (!password || busy || !pendingNewPw.current) return
    setError(null)
    setBusy(true)
    try {
      const tokens = await completeNewPassword(pendingNewPw.current, password)
      finish(tokens)
    } catch (e) {
      setBusy(false)
      const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', 'Something went wrong.')
      setError(err.code === 'InvalidPassword' ? err.message : mapErrorCopy(err))
    }
  }

  /**
   * Start the reset flow (BOARDROOM-2026-09-01-180926: reachable from BOTH
   * password steps). `returnTo` is the step a hard failure falls back to, so a
   * CREATE_PASSWORD user is never dumped onto a sign-in step they didn't come
   * from. Unknown emails stay NON-ENUMERATING: the consumer pool has
   * PreventUserExistenceErrors enabled, so ForgotPassword on an unknown email
   * returns a simulated success (no UserNotFound) — and if that guard ever
   * slipped, we defensively map UserNotFound to the SAME neutral success
   * outcome rather than surfacing "no account for that email".
   */
  async function beginForgot(returnTo: 'PASSWORD' | 'CREATE_PASSWORD' = 'PASSWORD') {
    setError(null)
    setNotice(null)
    setBusy(true)
    setStep('FORGOT_SEND')
    try {
      // Probe-resolved username (case-correct on the case-sensitive pool) —
      // NOT the raw email: a mis-cased ForgotPassword "succeeds" silently and
      // no code ever arrives (checklist #3).
      await forgotPassword(authUsername(), { exactUsername: true })
      setBusy(false)
      resetTransientAt('FORGOT_CODE')
      setNotice(FORGOT_SENT_NOTICE)
    } catch (e) {
      setBusy(false)
      const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', e instanceof Error ? e.message : 'Something went wrong.')
      if (err.code === 'UserNotFound') {
        // Neutral outcome — indistinguishable from success (anti-enumeration).
        resetTransientAt('FORGOT_CODE')
        setNotice(FORGOT_SENT_NOTICE)
        return
      }
      resetTransientAt(returnTo)
      fail(err)
    }
  }

  async function submitForgotCode() {
    if (code.trim().length < 6 || !password || busy) return
    setError(null)
    setBusy(true)
    try {
      // Same resolved username the reset code was issued to.
      await confirmForgotPassword(authUsername(), code.trim(), password, { exactUsername: true })
      setBusy(false)
      resetTransientAt('PASSWORD')
      setNotice('Password updated — sign in.')
    } catch (e) {
      setBusy(false)
      const err = e instanceof AuthFlowError ? e : new AuthFlowError('Unknown', 'Something went wrong.')
      if (err.code === 'CodeMismatch') {
        setError("That code isn't right — check the newest email.")
      } else if (err.code === 'InvalidPassword') {
        setError(err.message)
      } else {
        setError(mapErrorCopy(err))
      }
    }
  }

  function backToEmail() {
    createdPassword.current = null
    pendingNewPw.current = null
    resolvedUsername.current = null // never let a stale resolution target the OLD email's account
    resetTransientAt('EMAIL')
  }

  /**
   * Explicit "New to Trumin? Create an account" intent (BOARDROOM-2026-09-01-
   * 180926). Routes straight into the EXISTING create path (the neutral
   * SET_PASSWORD step) with NO probe and NO network call, so clicking it can
   * never reveal whether the email has an account. An existing email that comes
   * through here is still safe: the SET_PASSWORD crossover signs it in (or
   * hands it to the PASSWORD step) exactly as today. The implicit-creation
   * redesign is a separate item — this is intent signalling only.
   */
  function goCreateAccount() {
    if (!emailValid || busy) return
    createdPassword.current = null
    resetTransientAt('CREATE_PASSWORD')
  }

  // --- render helpers ------------------------------------------------------

  const enterSubmits = (fn: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      fn()
    }
  }

  const errorBlock = error ? (
    <p role="alert" className="secondary" data-testid="login-error" style={{ fontSize: 12.5, color: 'var(--error)', margin: 0 }}>
      {error}
    </p>
  ) : null
  const noticeBlock = notice ? (
    <p role="status" className="secondary" data-testid="login-notice" style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
      {notice}
    </p>
  ) : null

  const emailRow = (
    <div className="row" style={{ alignItems: 'center', gap: 8, justifyContent: 'center', fontSize: 12.5 }}>
      <span className="secondary" style={{ overflowWrap: 'anywhere' }}>{email.trim()}</span>
      <button type="button" data-testid="login-change-email" onClick={backToEmail} style={linkBtnStyle}>Change</button>
    </div>
  )

  function frame(children: React.ReactNode) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 20 }}>
        <div className="col" style={{ width: 360, gap: 22 }}>
          <div className="col" style={{ alignItems: 'center', gap: 14 }}>
            <Brand size={34} />
            <div className="col" style={{ alignItems: 'center', gap: 4, textAlign: 'center' }}>
              <h1 style={{ fontSize: 21 }}>My Trumin</h1>
              <p className="secondary" style={{ fontSize: 13, maxWidth: 300 }}>
                Your orders, tickets and race prep — one login for every start line.
              </p>
            </div>
          </div>
          {children}
        </div>
      </div>
    )
  }

  if (!configured) {
    return frame(
      <p className="faint" data-wired="false" role="status" style={{ fontSize: 12, textAlign: 'center', color: 'var(--warn)' }}>
        Sign-in is not configured in this build (no consumer pool values) —
        there is no demo mode; this app only shows real account data.
      </p>,
    )
  }

  // --- per-state UI --------------------------------------------------------

  let body: React.ReactNode

  if (step === 'EMAIL') {
    body = (
      <div className="col" style={{ gap: 12 }} data-testid="login-step-email">
        <div className="col" style={{ gap: 6 }}>
          <label style={labelStyle} htmlFor="login-email">Email</label>
          <input
            id="login-email" data-testid="login-email" type="email" autoFocus autoComplete="email"
            inputMode="email" placeholder="you@email.com" value={email}
            onChange={(e) => setEmail(e.target.value)} onKeyDown={enterSubmits(submitEmail)} style={inputStyle}
          />
        </div>
        {errorBlock}
        {noticeBlock}
        <Button variant="primary" full onClick={submitEmail} disabled={!emailValid || busy}>Continue</Button>
        {/* Distinct create intent (BOARDROOM-2026-09-01-180926): a secondary
            affordance below the primary action, routing into the EXISTING
            create path with no probe/network — reveals nothing pre-submit. */}
        <div className="row" style={{ justifyContent: 'center', gap: 6, fontSize: 12.5 }}>
          <span className="secondary">New to Trumin?</span>
          <button
            type="button" data-testid="login-create-account" onClick={goCreateAccount}
            disabled={!emailValid || busy}
            style={{ ...linkBtnStyle, opacity: !emailValid || busy ? 0.5 : 1 }}
          >
            Create an account
          </button>
        </div>
        <p className="faint" style={{ fontSize: 11, textAlign: 'center', margin: 0 }}>
          Use the email you bought your tickets with — your orders connect to
          your verified email address.
        </p>
      </div>
    )
  } else if (step === 'CHECKING') {
    body = <p role="status" className="secondary" data-testid="login-checking" style={{ fontSize: 13, textAlign: 'center' }}>Checking your email…</p>
  } else if (step === 'AUTHING') {
    body = <p role="status" className="secondary" data-testid="login-authing" style={{ fontSize: 13, textAlign: 'center' }}>Signing you in…</p>
  } else if (step === 'FORGOT_SEND') {
    body = <p role="status" className="secondary" data-testid="login-forgot-send" style={{ fontSize: 13, textAlign: 'center' }}>Sending your reset code…</p>
  } else if (step === 'PASSWORD') {
    body = (
      <div className="col" style={{ gap: 12 }} data-testid="login-step-password">
        {emailRow}
        <div className="col" style={{ gap: 6 }}>
          <label style={labelStyle} htmlFor="login-password">Password</label>
          <input
            id="login-password" data-testid="login-password" type="password" autoFocus
            autoComplete="current-password" value={password}
            onChange={(e) => setPassword(e.target.value)} onKeyDown={enterSubmits(submitPassword)} style={inputStyle}
          />
          {/* Prominent, industry-standard placement: directly under the field
              (BOARDROOM-2026-09-01-180926), not buried below the button. */}
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 2 }}>
            <button type="button" data-testid="login-forgot" onClick={() => void beginForgot('PASSWORD')} style={linkBtnStyle}>
              Forgot your password?
            </button>
          </div>
        </div>
        {errorBlock}
        {noticeBlock}
        <Button variant="primary" full onClick={submitPassword} disabled={!password || busy}>
          {busy ? 'Signing you in…' : 'Sign in'}
        </Button>
      </div>
    )
  } else if (step === 'CREATE_PASSWORD') {
    // Neutral SET_PASSWORD framing (ClaimLanding pattern): works for a brand-NEW
    // buyer (we create the account) AND a returning one (we sign them in), since
    // the pool's PreventUserExistenceErrors means we can't know which up front.
    body = (
      <div className="col" style={{ gap: 12 }} data-testid="login-step-create">
        <h2 style={{ fontSize: 16, textAlign: 'center', margin: 0 }}>Enter your password</h2>
        {emailRow}
        <div className="col" style={{ gap: 6 }}>
          {/* BOARDROOM-2026-09-01-174401: "Set your password" / "Create new
              password" read to EXISTING customers as "my account is gone" —
              this step serves both cases, so headline + label stay neutral and
              the footnote below carries the two readings. (Supersedes the
              CMT-2026-08-14-038 t2 create-action label.) */}
          <label style={labelStyle} htmlFor="login-new-password">Password</label>
          <input
            id="login-new-password" data-testid="login-create-password" type="password" autoFocus
            autoComplete="new-password" value={password}
            onChange={(e) => setPassword(e.target.value)} onKeyDown={enterSubmits(submitCreatePassword)} style={inputStyle}
          />
          <p className="faint" style={{ fontSize: 11, margin: 0 }}>At least 8 characters.</p>
          {/* Same reset flow as the PASSWORD step (BOARDROOM-2026-09-01-180926):
              this step serves existing customers too (ambiguous probe), so the
              forgot affordance must be HERE, directly under the field — not
              hidden behind a failed continue. A hard failure returns to THIS
              step, and unknown emails resolve neutrally (see beginForgot). */}
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 2 }}>
            <button type="button" data-testid="login-forgot" onClick={() => void beginForgot('CREATE_PASSWORD')} style={linkBtnStyle}>
              Forgot your password?
            </button>
          </div>
        </div>
        {errorBlock}
        {noticeBlock}
        <Button variant="primary" full onClick={submitCreatePassword} disabled={!password || busy}>
          {busy ? 'One moment…' : 'Continue'}
        </Button>
        <p className="faint" style={{ fontSize: 11, textAlign: 'center', margin: 0 }}>
          New here? This creates your account. Already have one? Enter your
          existing password to sign in.
        </p>
      </div>
    )
  } else if (step === 'CONFIRM_CODE') {
    body = (
      <div className="col" style={{ gap: 12 }} data-testid="login-step-confirm">
        <h2 style={{ fontSize: 16, textAlign: 'center', margin: 0 }}>Check your email</h2>
        <p className="secondary" style={{ fontSize: 12.5, textAlign: 'center', margin: 0 }}>
          We sent a 6-digit code to {maskEmail(email.trim())}.
        </p>
        <div className="col" style={{ gap: 6 }}>
          <label style={labelStyle} htmlFor="login-code">Confirmation code</label>
          <input
            id="login-code" data-testid="login-code" type="text" autoFocus inputMode="numeric"
            autoComplete="one-time-code" maxLength={6} placeholder="123456" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} onKeyDown={enterSubmits(submitConfirmCode)} style={inputStyle}
          />
        </div>
        {errorBlock}
        {noticeBlock}
        <Button variant="primary" full onClick={submitConfirmCode} disabled={code.trim().length < 6 || busy}>
          {busy ? 'Confirming…' : 'Confirm'}
        </Button>
        <div className="row" style={{ justifyContent: 'center' }}>
          <Button variant="ghost" small onClick={doResend} disabled={resend > 0 || busy}>
            {resend > 0 ? `Resend code (${resend}s)` : 'Resend code'}
          </Button>
        </div>
      </div>
    )
  } else if (step === 'NEW_PASSWORD') {
    body = (
      <div className="col" style={{ gap: 12 }} data-testid="login-step-newpassword">
        <h2 style={{ fontSize: 16, textAlign: 'center', margin: 0 }}>Set a new password</h2>
        <p className="secondary" style={{ fontSize: 12.5, textAlign: 'center', margin: 0 }}>
          Your account needs a new password to continue.
        </p>
        <div className="col" style={{ gap: 6 }}>
          <label style={labelStyle} htmlFor="login-set-password">New password</label>
          <input
            id="login-set-password" data-testid="login-set-password" type="password" autoFocus
            autoComplete="new-password" value={password}
            onChange={(e) => setPassword(e.target.value)} onKeyDown={enterSubmits(submitNewPassword)} style={inputStyle}
          />
          <p className="faint" style={{ fontSize: 11, margin: 0 }}>At least 8 characters.</p>
        </div>
        {errorBlock}
        <Button variant="primary" full onClick={submitNewPassword} disabled={!password || busy}>
          {busy ? 'Saving…' : 'Save and continue'}
        </Button>
      </div>
    )
  } else if (step === 'FORGOT_CODE') {
    body = (
      <div className="col" style={{ gap: 12 }} data-testid="login-step-forgot">
        <h2 style={{ fontSize: 16, textAlign: 'center', margin: 0 }}>Reset your password</h2>
        <p className="secondary" style={{ fontSize: 12.5, textAlign: 'center', margin: 0 }}>
          Enter the code we emailed to {maskEmail(email.trim())} and choose a new password.
        </p>
        <div className="col" style={{ gap: 6 }}>
          <label style={labelStyle} htmlFor="login-forgot-code">Reset code</label>
          <input
            id="login-forgot-code" data-testid="login-forgot-code" type="text" autoFocus inputMode="numeric"
            autoComplete="one-time-code" maxLength={6} placeholder="123456" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} style={inputStyle}
          />
        </div>
        <div className="col" style={{ gap: 6 }}>
          <label style={labelStyle} htmlFor="login-forgot-newpw">New password</label>
          <input
            id="login-forgot-newpw" data-testid="login-forgot-password" type="password"
            autoComplete="new-password" value={password}
            onChange={(e) => setPassword(e.target.value)} onKeyDown={enterSubmits(submitForgotCode)} style={inputStyle}
          />
          <p className="faint" style={{ fontSize: 11, margin: 0 }}>At least 8 characters.</p>
        </div>
        {errorBlock}
        {noticeBlock}
        <Button variant="primary" full onClick={submitForgotCode} disabled={code.trim().length < 6 || !password || busy}>
          {busy ? 'Updating…' : 'Update password'}
        </Button>
        <div className="row" style={{ justifyContent: 'center' }}>
          <Button variant="ghost" small onClick={doResend} disabled={resend > 0 || busy}>
            {resend > 0 ? `Resend code (${resend}s)` : 'Resend code'}
          </Button>
        </div>
      </div>
    )
  }

  return frame(body)
}

/** Map a normalised auth error to consumer-facing copy. InvalidPassword is
 *  handled at the call sites (server message rendered verbatim). */
function mapErrorCopy(err: AuthFlowError): string {
  switch (err.code) {
    case 'NotAuthorized':
      // Never "email or password" — by the time we sign in we already know the
      // account exists, so only the password can be wrong.
      return 'That password isn’t right — try again, or reset it below.'
    case 'UserNotFound':
      return "We couldn't find an account for that email."
    case 'CodeMismatch':
      return "That code isn't right — check the newest email."
    case 'ExpiredCode':
      return 'That code has expired — tap Resend code for a fresh one.'
    case 'LimitExceeded':
    case 'TooManyRequests':
      return 'Too many attempts — please wait a moment and try again.'
    case 'Network':
      return 'Network problem — check your connection and try again.'
    case 'InvalidPassword':
    case 'InvalidParameter':
      return err.message
    default:
      return err.message || 'Something went wrong. Please try again.'
  }
}
